import { RAG } from 'features/dashboard/charts/scoreColour'
import { formatDay } from './remedialDates'
import { SEVERITY_ORDER, severityMeta } from './remedialMeta'

export const SOURCE_ORDER = ['guru', 'incident', 'task_form', 'manual', 'imported']

export const SOURCE_NAMES = {
  guru: 'From documents',
  incident: 'From incidents',
  task_form: 'From task forms',
  manual: 'Added manually',
  imported: 'Imported from previous system',
}

const EMPTY_FIGURES = {
  live: 0,
  overdue: 0,
  awaitingReview: 0,
  opened: 0,
  resolved: 0,
  resolvedOnTime: 0,
  resolvedLate: 0,
  resolvedWithoutDueDate: 0,
  suggestedInReview: 0,
  dismissedInReview: 0,
  dismissedAfterAccept: 0,
  onTimeRate: null,
  avgResolveHours: null,
}

export const rateLevel = rate => {
  if (rate === null || rate === undefined) return null
  if (rate >= 80) return 'low'
  if (rate >= 50) return 'medium'
  return 'critical'
}

export const rateColour = rate => {
  const level = rateLevel(rate)
  return level ? RAG[level].fg : undefined
}

export const NOT_ENOUGH_DATA = 'Not enough data yet'

export const RAISED_EXPLAIN = 'Remedials raised between the dates you chose and accepted onto the list. Problems our AI found in documents only count once someone has reviewed and accepted them. Ones still waiting for review, and ones turned down at review, are not counted.'

export const SOURCE_FILTER_OPTIONS = [
  { value: null, label: 'All sources', color: null, description: 'Show remedials from every source' },
  ...SOURCE_ORDER.map(source => ({ value: source, label: SOURCE_NAMES[source], color: null, description: `Only remedials ${SOURCE_NAMES[source].toLowerCase()}` })),
]

export const SOURCE_FILTER_INFO = [
  { value: 'guru', label: 'From documents', description: 'Problems our AI found in uploaded documents, such as fire risk assessments. They only count once someone has reviewed and accepted them' },
  { value: 'incident', label: 'From incidents', description: 'Raised from a reported incident or accident' },
  { value: 'task_form', label: 'From task forms', description: 'Raised from an answer on a completed task form' },
  { value: 'manual', label: 'Added manually', description: 'Added by someone by hand' },
  { value: 'imported', label: 'Imported from previous system', description: 'Brought across from the system you used before' },
]

export const notCountedSentence = totals => {
  const waiting = totals?.suggestedInReview || 0
  const turnedDown = totals?.dismissedInReview || 0
  if (!waiting && !turnedDown) return null
  const parts = []
  if (waiting) parts.push(`${waiting.toLocaleString('en-GB')} AI ${waiting === 1 ? 'suggestion is' : 'suggestions are'} still waiting for review`)
  if (turnedDown) parts.push(`${turnedDown.toLocaleString('en-GB')} ${turnedDown === 1 ? 'was' : 'were'} turned down at review`)
  return `Not counted above: ${parts.join(' and ')}. They only become remedials once someone accepts them.`
}

export const formatRate = rate => (rate === null || rate === undefined ? NOT_ENOUGH_DATA : `${rate}%`)

export const formatHours = hours => {
  if (hours === null || hours === undefined) return NOT_ENOUGH_DATA
  if (hours < 1) return 'Under an hour'
  if (hours < 48) return `${Math.round(hours)} hours`
  return `${Math.round(hours / 24)} days`
}

export const rateExplanation = totals => {
  const t = { ...EMPTY_FIGURES, ...totals }
  const counted = t.resolvedOnTime + t.resolvedLate
  const leftOut = t.resolvedWithoutDueDate > 0
    ? ` ${t.resolvedWithoutDueDate} resolved without a due date ${t.resolvedWithoutDueDate === 1 ? 'is' : 'are'} left out, because there was no date to be on time for.`
    : ''
  if (counted === 0) return `No remedial with a due date was resolved between these dates, so there is no rate yet.${leftOut}`
  return `Resolved on time divided by every resolved remedial that had a due date: ${t.resolvedOnTime} on time out of ${counted}. 80% or more shows green, 50% to 79% amber and under 50% red.${leftOut}`
}

export const performanceCards = totals => {
  const t = { ...EMPTY_FIGURES, ...totals }
  return [
    { key: 'opened', label: 'Raised', value: t.opened, view: null, level: null, explain: RAISED_EXPLAIN },
    { key: 'resolved', label: 'Resolved', value: t.resolved, view: 'resolved', level: null, explain: 'Remedials put right between the dates you chose, either because all their tasks were completed or because someone marked them complete. Dismissed remedials are not counted.' },
    { key: 'resolvedOnTime', label: 'Resolved on time', value: t.resolvedOnTime, view: null, level: null, explain: 'Resolved on or before the due date they had on the day they were resolved. Once a task was handling a remedial, that is the latest deadline of its unfinished tasks.' },
    { key: 'overdue', label: 'Overdue now', value: t.overdue, view: 'overdue', level: t.overdue > 0 ? 'critical' : null, explain: 'Open remedials whose due date has passed, as things stand today. This number does not change with the dates you chose.' },
    { key: 'onTimeRate', label: 'On-time rate', value: formatRate(t.onTimeRate), view: null, level: rateLevel(t.onTimeRate), explain: rateExplanation(t) },
    { key: 'avgResolveHours', label: 'Average time to resolve', value: formatHours(t.avgResolveHours), view: null, level: null, explain: 'The average time from a remedial being raised to it being resolved, for remedials resolved between the dates you chose. Remedials brought across from the previous system without a record of when they were raised are left out.' },
  ]
}

const tint = level => value => (value ? RAG[level].fg : undefined)

export const COUNT_COLUMNS = [
  { key: 'opened', label: 'Raised', title: RAISED_EXPLAIN },
  { key: 'resolved', label: 'Resolved', title: 'Remedials resolved between the chosen dates.', drill: 'resolved' },
  { key: 'resolvedOnTime', label: 'On time', title: 'Resolved on or before their due date.', colour: tint('low') },
  { key: 'resolvedLate', label: 'Late', title: 'Resolved after their due date.', colour: tint('medium') },
  { key: 'overdue', label: 'Overdue now', title: 'Open and past their due date today.', drill: 'overdue', colour: tint('critical') },
  { key: 'live', label: 'Open now', title: 'Still being dealt with today: no task yet, task created or task in progress.', drill: 'all_open' },
  { key: 'onTimeRate', label: 'On-time %', title: 'On time divided by every resolved remedial that had a due date.', percent: true, colour: rateColour },
]

export const SITE_COLUMNS = COUNT_COLUMNS.filter(column => ['opened', 'resolved', 'overdue', 'live', 'onTimeRate'].includes(column.key))

export const severityRows = rows => SEVERITY_ORDER.map((level, index) => ({
  ...EMPTY_FIGURES,
  ...((rows || []).find(row => row.severity === level) || {}),
  severity: level,
  name: severityMeta(level).label,
  rank: SEVERITY_ORDER.length - index,
}))

const OPTIONAL_SOURCES = ['imported']

const hasFigures = row => Boolean(row && (row.opened || row.resolved || row.live || row.overdue || row.awaitingReview))

export const sourceRows = rows => SOURCE_ORDER
  .map((source, index) => ({
    ...EMPTY_FIGURES,
    ...((rows || []).find(row => row.source === source) || {}),
    source,
    name: SOURCE_NAMES[source],
    rank: SOURCE_ORDER.length - index,
  }))
  .filter(row => !OPTIONAL_SOURCES.includes(row.source) || hasFigures(row))

export const isEmptyPerformance = data => {
  if (!data?.totals) return false
  const t = { ...EMPTY_FIGURES, ...data.totals }
  return !t.opened && !t.resolved && !t.live && !t.awaitingReview && !(data.byBuilding || []).length
}

export const scopeExtra = scope => {
  if (!scope?.id) return {}
  if (scope.type === 'bp') return { bp: scope.id }
  if (scope.type === 'portfolio') return { portfolio: scope.id }
  if (scope.type === 'estate') return { estate: scope.id }
  return {}
}

export const performanceParams = (scope, range, source = null) => ({
  ...(range?.from ? { from: range.from, to: range.to } : {}),
  ...(source ? { source } : {}),
  ...(scope?.type === 'portfolio' && scope.id ? { portfolioId: scope.id } : {}),
  ...(scope?.type === 'estate' && scope.id ? { estateId: scope.id } : {}),
  ...(scope?.type === 'bp' && scope.id ? { buildingPassportId: scope.id } : {}),
})

export const periodSentence = range => (range?.from
  ? `Counting remedials raised or resolved between ${formatDay(range.from)} and ${formatDay(range.to)}. Overdue and open figures are as they stand today. Only buildings with task management switched on are counted, and dormant buildings are left out.`
  : 'Counting remedials raised or resolved at any time. Overdue and open figures are as they stand today. Only buildings with task management switched on are counted, and dormant buildings are left out.')

export const hubCardCounts = stats => {
  const views = stats?.views || {}
  const severity = stats?.bySeverityLive || stats?.bySeverityOpen || {}
  return {
    open: views.allOpen ?? 0,
    overdue: views.overdue ?? stats?.overdueOpen ?? 0,
    needsDecision: views.needsDecision ?? 0,
    dueThisWeek: views.dueThisWeek ?? 0,
    resolved: views.resolved ?? 0,
    dismissed: views.dismissed ?? 0,
    critical: severity.critical ?? 0,
    high: severity.high ?? 0,
    medium: severity.medium ?? 0,
    low: severity.low ?? 0,
  }
}
